import { type FC, type MouseEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { useRecoilCallback, useRecoilValue } from 'recoil'
import {
  isEditorItemType,
  isEditorTextBoxItemType,
} from '@/utils/editor-text-box/editorTextBoxAdapter'

import {
  EDITOR_TEXT_BOX_LOCK_FLIPS_IN_TOOLBAR,
  editorItemLockedSelector,
  editorTextBoxLocksAtom,
} from '@/atoms/editorTextBoxAtoms'
import { Tooltip } from '@/components/global/Tooltip/Tooltip'
import { Icon } from '@/components/priority/Icon/Icon'
import {
  ControlToolbar,
  HideInPdfBadge,
  ItemWrapper,
  SelectionBorder,
  WarningBar,
} from '@/components/page/Items/ItemControlOverlay/ItemControlOverlay.styled'
import { ClassicSelectedItemControls } from '@/components/page/Items/ItemControlOverlay/classic/ClassicSelectedItemControls'
import {
  CommonControls,
  DragControls,
  DuplicateControls,
  DeleteControls,
  ResizeCornerControls,
} from '@/components/page/Items/ItemControlOverlay/controls/CommonControls'
import { EdgeResizeControls } from '@/components/page/Items/ItemControlOverlay/controls/EdgeResizeControls'
import { TextControls } from '@/components/page/Items/ItemControlOverlay/controls/TextControls'
import { WarningIndicators } from '@/components/page/Items/ItemControlOverlay/controls/WarningIndicators'
import { useEditDockSide } from '@/components/page/Items/ItemControlOverlay/useEditDockSide'
import type { ItemControlState } from '@/types/itemControls'

type SelectedItemControlsProps = {
  itemId: string
  controlState: ItemControlState
  zoom: number
}

/**
 * Renders all controls for a single selected item. An editor text box or
 * rule gets the editor chrome below; every classic (TinyMCE-era) item keeps
 * the staging chrome, `classic/ClassicSelectedItemControls.tsx`.
 */
export const SelectedItemControls: FC<SelectedItemControlsProps> = (props) =>
  isEditorItemType(props.controlState.type) ? (
    <EditorSelectedItemControls {...props} />
  ) : (
    <ClassicSelectedItemControls {...props} />
  )

/**
 * The chrome of a selected editor text box or rule.
 *
 * Layout:
 * - Toolbar: horizontal bar centered above the item with action icons
 *   Order: Lock/Unlock | Text controls (text box only) | Drag | Duplicate |
 *   Delete
 *   For an editor text box the bar stays up DURING editing too (it coexists
 *   with the sticky text toolbar); when its above-the-box spot would overlap
 *   the sticky pill it docks to the BOTTOM of the box (useEditDockSide),
 *   clear of the bottom edge-resize pill. The Drag handle moves the box
 *   mid-edit without ending the session (Item.js keeps the box's Draggable
 *   enabled while editing, cancel-scoped to the box interior).
 * - Warning bar: vertical stack on the right side of the item
 * - Resize handles: all four corners + edge pills (none while the item is
 *   locked)
 *
 * The lock behind the chrome is `editorItemLockedSelector`: for an editor
 * text box the page's lock setting, unless this session set the box's own
 * lock. The lock icon shows it; the flip stays wired behind
 * `EDITOR_TEXT_BOX_LOCK_FLIPS_IN_TOOLBAR`, off. The asset lock hides
 * everything from a non-admin whatever the lock says.
 */
const EditorSelectedItemControls: FC<SelectedItemControlsProps> = ({
  itemId,
  controlState,
  zoom,
}) => {
  const { t } = useTranslation()
  const { type, position, selection, image, text, warnings } = controlState
  const { x = 0, y = 0, width = 0, height = 0, rotate = 0 } = position || {}
  const {
    isEditing = false,
    disabled = false,
    assetLocked = false,
  } = selection || {}

  const isEditorTextBoxItem = isEditorTextBoxItemType(type)

  const locked = useRecoilValue(editorItemLockedSelector(itemId))
  // A flip is session state, not a page change, so it never arms the
  // unsaved flag.
  const flipEditorTextBoxLock = useRecoilCallback(
    ({ set }) =>
      () =>
        set(editorTextBoxLocksAtom, (prev) => ({ ...prev, [itemId]: !locked })),
    [itemId, locked],
  )

  const showControls =
    (!isEditing || isEditorTextBoxItem) && !disabled && !assetLocked

  // Resize handle should remain visible during editing (only hide when disabled/locked)
  const showResizeHandle = !disabled && !assetLocked

  const { barRef, dockSide } = useEditDockSide({
    active: isEditorTextBoxItem && isEditing && showControls,
    x,
    y,
    width,
    height,
    zoom,
  })

  // Mousedown's default action moves focus, which blurs the box and ends the
  // edit session, so only the action icons let it through.
  const guardEditChrome = (e: MouseEvent<HTMLDivElement>) => {
    if (!isEditing) return
    if ((e.target as HTMLElement).closest('[data-overlay-action]')) return
    e.preventDefault()
  }

  const hasWarnings = warnings?.showBleedWarning === true

  return (
    <ItemWrapper
      $x={x}
      $y={y}
      $width={width}
      $height={height}
      $rotate={rotate}
      data-item-id={itemId}
      data-item-control-overlay
    >
      <SelectionBorder
        data-selection-border
        $convertedToEditable={text?.convertedToEditable}
        $editing={isEditing}
      />

      {showResizeHandle && (
        <ResizeCornerControls
          itemId={itemId}
          itemsLocked={locked}
          zoom={zoom}
          width={width}
          height={height}
        />
      )}

      {/* Hide in PDF badge */}
      {controlState.hideInPdf && (
        <HideInPdfBadge $zoom={zoom}>
          <Tooltip
            text={t('editor.menus.properties.elements.hideInPdfLabel')}
            placement="right"
            offsetX={10}
            width="120px"
          >
            <Icon icon="eyeSlash" width="16px" height="16px" />
          </Tooltip>
        </HideInPdfBadge>
      )}

      {showControls && (
        <ControlToolbar
          ref={barRef}
          $zoom={zoom}
          $dockSide={dockSide}
          data-control-toolbar
          data-dock-side={dockSide}
          onMouseDown={guardEditChrome}
        >
          <CommonControls
            itemsLocked={locked}
            onFlip={
              isEditorTextBoxItem && EDITOR_TEXT_BOX_LOCK_FLIPS_IN_TOOLBAR
                ? flipEditorTextBoxLock
                : undefined
            }
          />
          {isEditorTextBoxItem && (
            <TextControls
              itemId={itemId}
              textState={text}
              serviceTemplateIcon="squaresPlusIcon"
            />
          )}
          <DragControls itemId={itemId} itemsLocked={locked} />
          <DuplicateControls itemId={itemId} type={type} itemsLocked={locked} />
          <DeleteControls itemId={itemId} itemsLocked={locked} />
        </ControlToolbar>
      )}

      {/* Warning indicators on right side */}
      {hasWarnings && (
        <WarningBar $zoom={zoom} data-warning-bar>
          <WarningIndicators
            itemId={itemId}
            itemType={type}
            imageState={image}
            warnings={warnings}
          />
        </WarningBar>
      )}

      {showResizeHandle && (
        <EdgeResizeControls itemId={itemId} itemsLocked={locked} zoom={zoom} />
      )}
    </ItemWrapper>
  )
}
